import { defineStore } from 'pinia';
import { cloneDeep, get, set as lodashSet, each, find, map, has, filter } from 'lodash';
import Vue from 'vue';

import PointOfSaleApi from '@/extensions/shop/store/api/pos.js';
import CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';
import MemberTagApi from '@/extensions/restrictedArea/store/api/memberTag';
import StockApi from '@/extensions/shop/store/api/stock';

import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

const defaultForm = {
    key: '',
    name: '',
    color: undefined,
    assign_member_tags: false,
    use_stock_address_for_members: false,
    address: undefined,
    stocks: [],
    stock: undefined,
    customerGroup: undefined,
    assignableMemberTags: [],
    stockAddressMemberTags: [],
};

const defaultUi = {
    form: {
        initialized: false,
        validateOnEnter: false,
    }
};

export const useShopPosStore = defineStore('shopPos', {
    state: () => ({
        loading: true,
        itemsPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            stock: {
                loading: false,
                items: []
            },
            memberTag: {
                loading: false,
                items: []
            },
            customerGroup: {
                loading: false,
                items: []
            }
        },
        form: cloneDeep(defaultForm),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => get(state, 'ui.form.initialized'),
        formValidateOnEnter: (state) => get(state, 'ui.form.validateOnEnter'),
        rules: (state) => {
            let rules = {
                name: 'required',
                key: 'required',
            };

            if (state.form.use_stock_address_for_members) {
                rules.stockAddressMemberTags = 'required';
                rules.stock = 'required';
            }

            if (state.form.assign_member_tags) {
                rules.assignableMemberTags = 'required';
            }

            return rules;
        }
    },

    actions: {
        addItemsPaginatedToState(data) {
            this.itemsPaginated.items = data.items;
            this.itemsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.itemsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.itemsPaginated.link = cloneDeep(data.config.link);
            this.itemsPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.itemsPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.itemsPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.itemsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.itemsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.itemsPaginated.pageInfo.currentPage = currentPage;
        },

        defineSorting({ key, value }) {
            Vue.set(this.itemsPaginated.variables, 'order', key);
            Vue.set(this.itemsPaginated.variables, 'direction', value);
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.itemsPaginated.filters, key);
            } else {
                Vue.set(this.itemsPaginated.filters, key, value);
            }
        },

        definePathValue(value) {
            Vue.set(this.itemsPaginated, 'path', value);
        },

        defineFilterVisibility(value) {
            Vue.set(this.itemsPaginated, 'showFilters', value);
        },

        updateLoading(value) {
            this.loading = value;
        },

        insertPointOfSale(pointOfSale) {
            Vue.set(this, 'form', pointOfSale);
        },

        removePointOfSaleById(id) {
            if (this.form && this.form.id === id) {
                this.resetForm();
            }
        },

        resetForm() {
            Vue.set(this, 'form', cloneDeep(defaultForm));
        },

        updateForm({ path, value }) {
            const parts = path.toString().split('.');
            const lastKey = parts.pop();
            const parent = parts.length ? get(this.form, parts.join('.')) : this.form;
            if (parent) {
                Vue.set(parent, lastKey, value);
            }
        },

        addStock(stock) {
            let result = find(this.form.stocks, function (n) {
                return n.id == stock.id;
            });
            if (!result) {
                this.form.stocks.push(stock);
            }
        },

        removeStock(stockId) {
            var newStocks = filter(this.form.stocks, function (stock) {
                return stock.id !== stockId;
            });
            this.form.stocks = newStocks;
        },

        updateStockOptionsLoading(value) {
            Vue.set(this.options.stock, 'loading', value);
        },

        insertStockOptions(stocks) {
            Vue.set(this.options.stock, 'items', stocks);
        },

        updateMemberTagOptionsLoading(value) {
            Vue.set(this.options.memberTag, 'loading', value);
        },

        insertMemberTagOptions(tags) {
            Vue.set(this.options.memberTag, 'items', tags);
        },

        updateCustomerGroupOptionsLoading(value) {
            Vue.set(this.options.customerGroup, 'loading', value);
        },

        insertCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.customerGroup, 'items', customerGroups);
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        addFormAddress() {
            if (this.form.address && this.form.address.deleted) {
                Vue.delete(this.form.address, 'deleted');
            } else {
                Vue.set(this.form, 'address', {
                    type: 'main',
                    street: '',
                    zipcode: '',
                    city: '',
                    country: 'DE',
                    default_billing: true,
                    default_shipping: true,
                });
            }
        },

        removeFormAddress() {
            Vue.set(this.form.address, 'deleted', true);
        },

        setFormAddressGender({ value }) {
            Vue.set(this.form.address, 'gender', value);
        },

        setFormAddressFirstName({ value }) {
            Vue.set(this.form.address, 'first_name', value);
        },

        setFormAddressLastName({ value }) {
            Vue.set(this.form.address, 'last_name', value);
        },

        setFormAddressEmail({ value }) {
            Vue.set(this.form.address, 'email', value);
        },

        setFormAddressPhone({ value }) {
            Vue.set(this.form.address, 'phone', value);
        },

        setFormAddressStreet({ value }) {
            Vue.set(this.form.address, 'street', value);
        },

        setFormAddressZipcode({ value }) {
            Vue.set(this.form.address, 'zipcode', value);
        },

        setFormAddressCity({ value }) {
            Vue.set(this.form.address, 'city', value);
        },

        setFormAddressCountry({ value }) {
            Vue.set(this.form.address, 'country', value);
        },

        setFormAddressCompany({ value }) {
            Vue.set(this.form.address, 'company', value);
        },

        setFormAddressCompanyAddition({ value }) {
            Vue.set(this.form.address, 'company_addition', value);
        },

        setFormAddressTaxId({ value }) {
            Vue.set(this.form.address, 'tax_id', value);
        },

        setFormAddressTaxNr({ value }) {
            Vue.set(this.form.address, 'tax_nr', value);
        },

        setFormAddressUrl({ value }) {
            Vue.set(this.form.address, 'url', value);
        },

        setFormLatitude({ value }) {
            Vue.set(this.form.address, 'latitude', value);
        },

        setFormLongitude({ value }) {
            Vue.set(this.form.address, 'longitude', value);
        },

        // Async actions
        fetchPaginatedItems(shopID, cmsID) {
            return PointOfSaleApi.fetchPointsOfSalePaginated(this.itemsPaginated, shopID, cmsID).then(data => {
                this.addItemsPaginatedToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchPointOfSale(posId) {
            return PointOfSaleApi.fetchPointOfSale(posId).then(data => {
                this.insertPointOfSale(data);
            });
        },

        fetchStockOptions(shopID) {
            this.updateStockOptionsLoading(true);
            return StockApi.fetchList(shopID)
                .then(stocks => {
                    this.updateStockOptionsLoading(false);
                    this.insertStockOptions(stocks);
                })
                .catch(err => {
                    this.updateStockOptionsLoading(false);
                    throw err;
                });
        },

        fetchMemberTagOptions(cmsID) {
            this.updateMemberTagOptionsLoading(true);
            return MemberTagApi.fetchList(cmsID)
                .then(tags => {
                    this.updateMemberTagOptionsLoading(false);
                    this.insertMemberTagOptions(tags);
                })
                .catch(err => {
                    this.updateMemberTagOptionsLoading(false);
                    throw err;
                });
        },

        fetchCustomerGroupOptions(shopID) {
            const setupStore = useSetupStore(pinia);
            if (has(setupStore, 'activeCms.shop.defaultCustomerGroup'))
                this.insertCustomerGroupOptions([setupStore.activeCms.shop.defaultCustomerGroup]);

            this.updateCustomerGroupOptionsLoading(true);
            return CustomerGroupApi.fetchList(shopID)
                .then(customerGroups => {
                    this.updateCustomerGroupOptionsLoading(false);
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch(err => {
                    this.updateCustomerGroupOptionsLoading(false);
                    throw err;
                });
        },

        create(pos) {
            const setupStore = useSetupStore(pinia);
            pos = cloneDeep(pos);

            const shop = setupStore.activeCms.shop;
            pos.shop = { id: shop.id };

            return PointOfSaleApi.create(pos).then((pos) => {
                this.insertPointOfSale(cloneDeep(pos));
                return pos;
            });
        },

        update(pos) {
            return PointOfSaleApi.update(pos).then((pos) => {
                this.insertPointOfSale(cloneDeep(pos));
                return pos;
            });
        },

        deletePointOfSale(id) {
            return PointOfSaleApi.deletePointOfSale(id).then((pos) => {
                this.removePointOfSaleById(id);
                return pos;
            });
        },

        registerFiskalyClient(pointOfSaleId) {
            return PointOfSaleApi.registerFiskalyClient(pointOfSaleId).then((pos) => {
                this.insertPointOfSale(cloneDeep(pos));
                return pos;
            });
        },
    }
});
